import { finishFocus } from "../lib/focusDissolve";
import { TACTILE_SETTLE, settleFrames, settleOptions } from "../lib/tactileSettle";
import { flushSync } from "react-dom";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Animate foreground children only: the material, wallpaper and rail stay still.
export const VESSEL_SELECTOR = [
  ".today-layout__vessel", ".journey-home__arrival", ".toto-workspaces__vessel", ".team-space__action",
  ".payroll-assembly__vessel", ".toto-profile__vessel", ".hiring-workspace__vessel",
  ".budget-workspace__vessel", ".journey-planning-space__vessel", ".eod-space__vessel",
  ".documents-space__vessel", ".operations-space__vessel", ".inventory-space__vessel",
  ".eg-review__field", ".toto-review-evidence:not(.is-embedded) > .toto-review-evidence__plane",
  ".payroll-accepted-flow > .payroll-team-plane", ".eg-schedule-space__vessel",
  '[data-testid="app-shell"] > main',
].join(",");

export const VESSEL_MOTION = TACTILE_SETTLE;
const isTodayRoute = path => ['/home','/my-day','/today/team','/today/goals','/eod','/operations/eod'].includes(path);
const isVesselRoute = (path) => !/^\/(login|offer|witness|sign|apply|tour|lab|prototypes|present)(\/|$)/.test(path);
export const vesselForeground = () => {
  const vessel = document.querySelector(VESSEL_SELECTOR);
  const todayContent = vessel?.querySelector(':scope > [data-today-content]');
  if(todayContent)return [todayContent];
  const slot = vessel?.querySelector(':scope > [data-vessel-content-slot]');
  if (slot) return [slot];
  return vessel ? [...vessel.children].filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true") : [];
};

const scrollNodes = () => {
  const vessel = document.querySelector(VESSEL_SELECTOR);
  return vessel ? [vessel, ...vessel.querySelectorAll("*")] : [];
};
const captureScroll = () => ({
  window: [window.scrollX, window.scrollY],
  nodes: scrollNodes().map((element, index) => ({ index, top: element.scrollTop, left: element.scrollLeft }))
    .filter(position => position.top || position.left),
});
const restoreScroll = (snapshot) => {
  const nodes = scrollNodes();
  nodes.forEach(element => { if (element.scrollTop || element.scrollLeft) { element.scrollTop = 0; element.scrollLeft = 0; } });
  snapshot?.nodes.forEach(({ index, top, left }) => {
    if (nodes[index]) { nodes[index].scrollTop = top; nodes[index].scrollLeft = left; }
  });
  if (window.scrollX || window.scrollY || snapshot?.window.some(Boolean)) window.scrollTo(...(snapshot?.window || [0, 0]));
};

export default function useVesselTransition(location, enabled, navigationType = "PUSH") {
  const [displayed, setDisplayed] = useState(location);
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const current = useRef(location);
  const animations = useRef([]);
  const pendingEntrance = useRef(false);
  const pendingPresentation = useRef(false);
  const pendingScroll = useRef(false);
  const navigationFocus = useRef(null);
  const scrollPositions = useRef(new Map());
  const baseTransforms = useRef(new Map());
  const restoreFocus = useRef(false);
  const inertElements = useRef([]);
  const entranceObserver = useRef(null);
  const interruptedOpacity = useRef(new Map());

  const cancel = useCallback(() => {
    entranceObserver.current?.disconnect();
    entranceObserver.current = null;
    animations.current.forEach((animation) => animation.cancel());
    animations.current = [];
    inertElements.current.forEach(([element, inert]) => { element.inert = inert; });
    inertElements.current = [];
  }, []);

  useLayoutEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useLayoutEffect(() => {
    cancel();
    let obsolete = false;
    const previous = current.current;
    const targets = vesselForeground();
    const sameContent = previous.pathname === location.pathname
      && (previous.search || "") === (location.search || "");
    // Snapshot the displayed entry before a permitted route change commits.
    if (previous !== location) scrollPositions.current.set(previous.key || previous.pathname + (previous.search || ""), captureScroll());
    const focused = document.activeElement;
    if (previous !== location) {
      restoreFocus.current = !sameContent && targets.some(element => element.contains(focused));
      navigationFocus.current = focused?.closest?.(".continuous-vessel-primary") ? focused.getAttribute("aria-label") : null;
    }
    const commit = (animate) => {
      if (obsolete) return;
      pendingEntrance.current = animate;
      pendingPresentation.current = previous !== location;
      pendingScroll.current = !sameContent || navigationType === "POP";
      current.current = location;
      if (animate) {
        // Keep the exit fill until the new route commits and its entrance is
        // installed. Releasing it before React commits exposes the old page.
        flushSync(() => setDisplayed(location));
      } else {
        cancel();
        setDisplayed(location);
      }
    };
    if (isTodayRoute(previous.pathname) && isTodayRoute(location.pathname)) {
      commit(false);
    } else if (!enabled || reduced || sameContent
      || !isVesselRoute(previous.pathname) || !isVesselRoute(location.pathname)
      || !targets.length || !targets.every((element) => typeof element.animate === "function")) {
      commit(false);
    } else {
      inertElements.current = targets.map((element) => [element, element.inert]);
      targets.forEach((element) => { element.inert = true; });
      animations.current = targets.map((element) => {
        const style = getComputedStyle(element);
        const base = baseTransforms.current.get(element) ?? style.transform;
        baseTransforms.current.set(element, base);
        return element.animate(
          settleFrames("exit", interruptedOpacity.current.get(element) ?? { opacity: style.opacity, transform: style.transform }, base),
          settleOptions("exit"),
        );
      });
      Promise.all(animations.current.map((animation) => finishFocus(animation)))
        .then(() => commit(true)).catch(() => {}); // Cancellation belongs to the newer destination.
    }
    interruptedOpacity.current.clear();
    return () => {
      obsolete = true;
      // Capture the presented opacity and position before cancelling interrupted motion.
      interruptedOpacity.current = new Map(animations.current.flatMap((animation) => {
        const target = animation.effect?.target;
        return target?.isConnected ? [[target, { opacity: getComputedStyle(target).opacity, transform: getComputedStyle(target).transform }]] : [];
      }));
      cancel();
    };
  }, [location, enabled, reduced, navigationType, cancel]);

  useLayoutEffect(() => {
    if (!pendingPresentation.current || !enabled || displayed !== current.current) return;
    const animate = pendingEntrance.current && !reduced;
    pendingEntrance.current = false;
    const arrive = () => {
      const targets = vesselForeground();
      if (!targets.length) return false;
      if (navigationFocus.current && !document.querySelector(".continuous-vessel-primary")) return false;
      cancel();
      baseTransforms.current.clear();
      pendingPresentation.current = false;
      if (pendingScroll.current) restoreScroll(navigationType === "POP" ? scrollPositions.current.get(displayed.key || displayed.pathname + (displayed.search || "")) : undefined);
      animations.current = animate ? targets.filter((element) => typeof element.animate === "function").map((element) => {
        const base = getComputedStyle(element).transform;
        baseTransforms.current.set(element, base);
        return element.animate(settleFrames("enter", {}, base), settleOptions("enter"));
      }) : [];
      const arrivals = [...animations.current];
      Promise.all(arrivals.map(finishFocus)).then(() => arrivals.forEach(animation => animation.cancel())).catch(() => {});
      if (navigationFocus.current) {
        const button = [...document.querySelectorAll(".continuous-vessel-primary button")].find(element => element.getAttribute("aria-label") === navigationFocus.current);
        button?.focus({ preventScroll: true });
        navigationFocus.current = null;
        restoreFocus.current = false;
      } else if (restoreFocus.current) {
        const heading = document.querySelector(VESSEL_SELECTOR)?.querySelector("h1, h2");
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          heading.focus({ preventScroll: true });
        }
        restoreFocus.current = false;
      }
      return true;
    };
    // Lazy routes may mount their Vessel after Suspense resolves.
    if (!arrive()) {
      entranceObserver.current = new MutationObserver(arrive);
      entranceObserver.current.observe(document.body, { childList: true, subtree: true });
    }
  }, [displayed, enabled, reduced, navigationType, cancel]);

  // Authentication and public routes never wait for a visual transition.
  return enabled ? displayed : location;
}
